// NGO registration form (Task 13.1).
//
// Inline validation delegates to the shared domain validator
// (validateNGORegistration) mirroring the backend rules (Req 4.1, 4.3). Entered
// values are retained on rejection and every violated field is flagged. On
// success the certificate is uploaded and the registerNGO callable is invoked.

import { useState } from "react";
import {
  FOOD_CATEGORIES,
  validateNGORegistration,
  type FoodCategory,
  type NGORegistrationField,
} from "@sharebite/domain";
import { Card } from "../../components/primitives";
import * as api from "../../lib/api";
import { formatBytes, toDocumentMeta } from "../../lib/fileMeta";

const FIELD_LABELS: Record<NGORegistrationField, string> = {
  ngoName: "NGO Name (1-200 characters) is required.",
  representativeName: "Representative Name (1-100 characters) is required.",
  phoneNumber: "Phone Number must be 10-15 digits.",
  address: "Address (1-300 characters) is required.",
  acceptedCategories: "Select 1 to 3 accepted food categories.",
  document: "Upload a PDF, JPEG, or PNG certificate up to 10 MB.",
  duplicateProfile: "An NGO profile already exists for this account.",
};

export function NGORegistrationForm({
  uid,
  onSubmitted,
}: {
  uid: string;
  onSubmitted: () => void;
}): JSX.Element {
  const [ngoName, setNgoName] = useState("");
  const [representativeName, setRepresentativeName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [categories, setCategories] = useState<FoodCategory[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [violations, setViolations] = useState<NGORegistrationField[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  function toggleCategory(c: FoodCategory): void {
    setCategories((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
    );
  }

  const has = (f: NGORegistrationField) => violations.includes(f);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitError(null);
    const payload = {
      ngoName,
      representativeName,
      phoneNumber,
      address,
      acceptedCategories: categories,
      document: file ? toDocumentMeta(file) : { format: "none", sizeBytes: 0 },
    };
    const result = validateNGORegistration(payload, false);
    if (!result.accepted) {
      setViolations(result.violations);
      return;
    }
    setViolations([]);
    setSubmitting(true);
    try {
      const verificationDocRef = await api.uploadVerificationDocument(
        uid,
        file as File,
      );
      await api.registerNGO({
        ...payload,
        acceptedCategories: categories,
        verificationDocRef,
      });
      onSubmitted();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Submission failed. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="sb-main">
      <Card title="NGO registration">
        <form onSubmit={handleSubmit} noValidate>
          <div className="sb-field">
            <label htmlFor="n-name">NGO Name</label>
            <input
              id="n-name"
              className="sb-input"
              value={ngoName}
              onChange={(e) => setNgoName(e.target.value)}
            />
            {has("ngoName") && (
              <span className="sb-field__error">{FIELD_LABELS.ngoName}</span>
            )}
          </div>

          <div className="sb-field">
            <label htmlFor="n-rep">Representative Name</label>
            <input
              id="n-rep"
              className="sb-input"
              value={representativeName}
              onChange={(e) => setRepresentativeName(e.target.value)}
            />
            {has("representativeName") && (
              <span className="sb-field__error">
                {FIELD_LABELS.representativeName}
              </span>
            )}
          </div>

          <div className="sb-field">
            <label htmlFor="n-phone">Phone Number</label>
            <input
              id="n-phone"
              className="sb-input"
              inputMode="numeric"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
            {has("phoneNumber") && (
              <span className="sb-field__error">{FIELD_LABELS.phoneNumber}</span>
            )}
          </div>

          <div className="sb-field">
            <label htmlFor="n-address">Address</label>
            <textarea
              id="n-address"
              className="sb-textarea"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
            {has("address") && (
              <span className="sb-field__error">{FIELD_LABELS.address}</span>
            )}
          </div>

          <div className="sb-field">
            <label>Accepted Food Categories (1-3)</label>
            <div className="sb-row">
              {FOOD_CATEGORIES.map((c) => (
                <label key={c} className="sb-pill" style={{ cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={categories.includes(c)}
                    onChange={() => toggleCategory(c)}
                  />{" "}
                  {c}
                </label>
              ))}
            </div>
            {has("acceptedCategories") && (
              <span className="sb-field__error">
                {FIELD_LABELS.acceptedCategories}
              </span>
            )}
          </div>

          <div className="sb-field">
            <label htmlFor="n-doc">
              Registration Certificate (PDF, JPEG, PNG, &le; 10 MB)
            </label>
            <input
              id="n-doc"
              type="file"
              className="sb-input"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file && (
              <span className="sb-muted">
                {file.name} ({formatBytes(file.size)})
              </span>
            )}
            {has("document") && (
              <span className="sb-field__error">{FIELD_LABELS.document}</span>
            )}
          </div>

          {has("duplicateProfile") && (
            <p className="sb-field__error">{FIELD_LABELS.duplicateProfile}</p>
          )}
          {submitError && <p className="sb-field__error">{submitError}</p>}

          <button type="submit" className="sb-btn" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit application"}
          </button>
        </form>
      </Card>
    </div>
  );
}
